/**
 * Reusable India Address Picker (PIN-first + Location-first).
 * Used inside Employee 360 to add / update delivery addresses.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Search, MapPin } from "lucide-react";

export function AddressPicker({ value, onChange }) {
  const [mode, setMode] = useState("pin"); // pin | location
  const [pin, setPin] = useState(value?.postal_code || "");
  const [pinMatches, setPinMatches] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    api.get("/locations/india/states").then(({ data }) => setStates(data.items || []));
  }, []);

  useEffect(() => {
    if (mode !== "location" || !value?.state_code) return;
    api.get("/locations/india/districts", { params: { state: value.state_code } })
      .then(({ data }) => setDistricts(data.items || []));
  }, [mode, value?.state_code]);

  useEffect(() => {
    if (mode !== "location" || !value?.state_code || !value?.district) return;
    api.get("/locations/india/cities", { params: { state: value.state_code, district: value.district } })
      .then(({ data }) => setCities(data.items || []));
  }, [mode, value?.state_code, value?.district]);

  const patch = (p) => onChange({ ...value, ...p });

  const lookupPin = async () => {
    if (!/^\d{6}$/.test(pin)) return;
    const { data } = await api.get(`/locations/india/pin/${pin}`);
    setPinMatches(data.matches || []);
    if (data.matches?.length === 1) applyPinMatch(data.matches[0]);
    else if (data.matches?.length === 0) patch({ postal_code: pin });
  };

  const applyPinMatch = (m) => {
    patch({
      postal_code: m.pin,
      state_code: m.state_code,
      state: states.find((s) => s.state_code === m.state_code)?.name,
      district: m.district,
      city: m.city,
      locality: m.locality,
    });
  };

  return (
    <div className="space-y-3" data-testid="address-picker">
      <div className="flex gap-1 text-xs">
        <button type="button" onClick={() => setMode("pin")} className={"h-8 px-3 rounded-md " + (mode === "pin" ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-700")} data-testid="mode-pin">PIN first</button>
        <button type="button" onClick={() => setMode("location")} className={"h-8 px-3 rounded-md " + (mode === "location" ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-700")} data-testid="mode-location">Location first</button>
      </div>

      {mode === "pin" ? (
        <div className="space-y-2">
          <label className="block">
            <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1 flex items-center gap-1"><Search className="h-3 w-3" /> PIN code</div>
            <div className="flex gap-2">
              <input className="input" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="600040" maxLength={6} data-testid="pin-first-input" />
              <button type="button" onClick={lookupPin} className="h-9 px-3 rounded-md bg-slate-900 text-white text-sm">Find</button>
            </div>
          </label>
          {pinMatches.length > 1 && (
            <div className="border border-slate-200 rounded-md divide-y">
              {pinMatches.map((m, i) => (
                <button type="button" key={i} onClick={() => applyPinMatch(m)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
                  <div className="font-medium">{m.post_office}</div>
                  <div className="text-xs text-slate-500">{m.locality} · {m.city} · {m.district} · {m.state_code}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <SelectField label="State" value={value?.state_code || ""} onChange={(v) => patch({ state_code: v, state: states.find((s) => s.state_code === v)?.name, district: "", city: "", locality: "" })}
            options={states.map((s) => [s.state_code, s.name])} testid="loc-state" />
          <SelectField label="District" value={value?.district || ""} onChange={(v) => patch({ district: v, city: "", locality: "" })}
            options={districts.map((d) => [d.name, d.name])} testid="loc-district" />
          <SelectField label="City" value={value?.city || ""} onChange={(v) => patch({ city: v })} options={cities.map((c) => [c.name, c.name])} testid="loc-city" />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <TextField label="Address line 1" value={value?.line1} onChange={(v) => patch({ line1: v })} testid="addr-line1" />
        <TextField label="Address line 2" value={value?.line2} onChange={(v) => patch({ line2: v })} testid="addr-line2" />
        <TextField label="Locality / Area" value={value?.locality} onChange={(v) => patch({ locality: v })} testid="addr-locality" />
        <TextField label="Landmark" value={value?.landmark} onChange={(v) => patch({ landmark: v })} testid="addr-landmark" />
        <TextField label="PIN" value={value?.postal_code} onChange={(v) => patch({ postal_code: v })} maxLength={6} testid="addr-pin" />
      </div>

      {value?.postal_code && !/^\d{6}$/.test(value.postal_code) && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1.5 flex items-center gap-1">
          <MapPin className="h-3 w-3" /> PIN must be 6 digits.
        </div>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, testid, ...rest }) {
  return (
    <label className="block">
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">{label}</div>
      <input className="input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...rest} />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">{label}</div>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        <option value="">—</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

export function ContactFields({ value, onChange }) {
  const patch = (p) => onChange({ ...value, ...p });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="contact-fields">
      <div className="grid grid-cols-[80px_1fr] gap-2">
        <TextField label="Country" value={value?.primary_contact_country_code || "+91"} onChange={(v) => patch({ primary_contact_country_code: v })} testid="prim-cc" />
        <TextField label="Primary mobile" value={value?.primary_contact} onChange={(v) => patch({ primary_contact: v })} testid="prim-num" />
      </div>
      <div className="grid grid-cols-[80px_1fr] gap-2">
        <TextField label="Country" value={value?.secondary_contact_country_code || "+91"} onChange={(v) => patch({ secondary_contact_country_code: v })} testid="sec-cc" />
        <TextField label="Secondary mobile" value={value?.secondary_contact} onChange={(v) => patch({ secondary_contact: v })} testid="sec-num" />
      </div>
      <TextField label="Secondary contact name" value={value?.secondary_contact_name} onChange={(v) => patch({ secondary_contact_name: v })} testid="sec-name" />
      <TextField label="Relationship (optional)" value={value?.secondary_contact_relationship} onChange={(v) => patch({ secondary_contact_relationship: v })} testid="sec-rel" />
    </div>
  );
}
